import {
  Icon,
  Menu,
  SaveSnackbar,
  Spacing,
  Spinner,
  useSnackbar,
  ExpandableSection,
} from "@mindlercare/ui-web";
import { defaultTheme } from "@mindlercare/ui-web/src/shared/constants";
import { useFlag } from "@mindlercare/unleash-client";
import { isForbiddenError } from "api/helpers";
import {
  useGetIcbtSequentialUnlock,
  useRemoveIcbtRequest,
} from "api/useIcbtQueries";
import { AssignIcbtModal } from "components/icbt/assignIcbtModal";
import { EmptyContainer } from "components/lib/EmptyContainer";
import { isPast, isToday } from "date-fns";
import { useCallback, useState } from "react";
import { TFunction, useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { styled } from "styled-components";
import { Typography } from "components/typography";
import { useMindlerApi } from "api/apiv2";
import { ReactQueryV5 } from "@mindlercare/mindlerapi-client";
const { useQuery } = ReactQueryV5;
import { usePsychologistUser } from "hooks";
import { useUserHasRole } from "../../../../hooks/userHooks";
import StatusIndicator from "../../../lib/statusIndicator";
import { Table } from "../../../Table";
import { AddButton } from "./AddButton";
import { ConfirmRemoveAssignModal } from "./modals/ConfirmRemoveAssignModal";
import EditFeedbackDateModal from "./modals/EditFeedbackDateModal";
import { FeedbackModal } from "./modals/FeedbackModal/FeedbackModal";
import { PreviewProgramModal } from "./modals/PreviewProgramModal";
import {
  AssignedTableRow,
  AssignedTableRowMetadata,
  getFeedbackLockReason,
  getIsSequentiallyLocked,
  getIsFeedbackLocked,
  getIsRequestOwner,
  getRequestingPsychologistId,
  useIcbtTableData,
} from "./useIcbtTableData";
import { useSortableTable } from "./useSortableTable";
import { getAssessment } from "./useIcbtTableData/utils";

const IcbtHeaderContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-end;
  height: fit-content;
  padding: 0 2px;
  margin-bottom: 10px;
`;

const getNotification = ({
  assignedNewProgressCount,
  feedbackOverDueCount,
  t,
}: {
  assignedNewProgressCount: number;
  feedbackOverDueCount?: number;
  t: TFunction<"translation", undefined>;
}) => {
  if (feedbackOverDueCount && feedbackOverDueCount > 0) {
    return {
      status: "alert",
      text: `${t(
        feedbackOverDueCount > 1
          ? "N_OVERDUE_FEEDBACK"
          : "ONE_OVERDUE_FEEDBACK",
        {
          count: feedbackOverDueCount,
        },
      )}`,
    } as const;
  }

  if (assignedNewProgressCount > 0) {
    return {
      status: "info",
      text: `${assignedNewProgressCount} ${t("RECENTLY_WORKED_ON")}`,
    } as const;
  }
  return undefined;
};

const POLLING_INTERVAL_MS = 5000;

export const Icbt = () => {
  const isPsychologist = useUserHasRole("Psychologist");
  const { patientUserId } = useParams<{ patientUserId: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const isJournalingV2Enabled = useFlag("JOURNALING_V2");
  const feedbackLockEnabled = useFlag(
    "ICBT_LOCK_FEEDBACK_UNTIL_PATIENT_ACTIVE",
  );

  const { psychologist } = usePsychologistUser();
  const loggedInPsychologistId = psychologist?.psychologistId;

  const apiv2 = useMindlerApi();
  const { data: patient } = useQuery(
    apiv2.patients.getByUserId.queryOptions(
      {
        patientUserId: Number(patientUserId),
      },
      {
        enabled: !!patientUserId,
      },
    ),
  );

  const { data: icbtSequentialUnlock } = useGetIcbtSequentialUnlock(
    {
      userId: patientUserId!,
    },
    {
      enabled: !!patientUserId && isJournalingV2Enabled,
      refetchInterval: (query) =>
        Array.isArray(query.state.data) && query.state.data.length > 0
          ? POLLING_INTERVAL_MS
          : false,
    },
  );

  const patientId = patient?.patientId;
  const userId = patient?.userId;

  // Open program preview modal ui state and handler
  const [programPreviewModalOpen, setProgramPreviewModalOpen] = useState(false);
  // TODO: fix when we have a way to ask for program by just it's id
  const [programAndTreatmentIdToPreview, setProgramIdAndTreatmentIdToPreview] =
    useState<{
      programId: string;
      treatmentId: string;
      icbtRequestId: string | null;
      feedbackDueDate: string | null;
      feedbackDueDateIgnored?: boolean;
    }>();

  // Open feedback modal ui state and handler
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);

  const handleClickOnRow = useCallback(
    ({
      programId,
      treatmentId,
      icbtRequestId,
      feedbackDueDate,
      feedbackDueDateIgnored,
    }: {
      programId: string;
      treatmentId: string;
      icbtRequestId: string | null;
      feedbackDueDate: string | null;
      feedbackDueDateIgnored?: boolean;
    }) => {
      setProgramIdAndTreatmentIdToPreview({
        programId,
        treatmentId,
        icbtRequestId,
        feedbackDueDate,
        feedbackDueDateIgnored,
      });
      setFeedbackModalOpen(true);
    },
    [],
  );

  const openPreviewModal = useCallback(
    ({ programId }: { programId: string }) => {
      setProgramIdAndTreatmentIdToPreview({
        programId,
        treatmentId: "null", // does not matter, only programId is used for the preview modal
        icbtRequestId: null,
        feedbackDueDate: null,
      });
      setProgramPreviewModalOpen(true);
    },
    [],
  );

  const handleCloseProgramPreviewModal = useCallback(() => {
    setProgramPreviewModalOpen(false);
    setFeedbackModalOpen(false);
    setProgramIdAndTreatmentIdToPreview(undefined);
  }, []);

  const {
    data,
    assignedTableColumns,
    selfExploredTableColumns,
    isLoading,
    possiblyErrors,
    isError,
  } = useIcbtTableData({
    patientId,
    userId,
    openPreviewModal,
    icbtSequentialUnlock,
  });

  const {
    hasDraggedItems,
    handleSortCancel,
    handleSortSave,
    data: tableData,
    Components: DndComponents,
    isMutating: isSortingMutating,
  } = useSortableTable({
    data: data.assignedTableRows,
    userId,
  });

  const [selectedRow, setSelectedRow] = useState<AssignedTableRow>();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [editFeedbackDateOpen, setEditFeedbackDateOpen] = useState<Pick<
    AssignedTableRowMetadata,
    "feedbackDate" | "requestId"
  > | null>(null);

  const [assignIcbtModalIsOpen, setAssignIcbtModalIsOpen] = useState(false);

  const handleAssignIcbtModalClose = useCallback(() => {
    setAssignIcbtModalIsOpen(false);
  }, []);

  const { mutateAsync: removeIcbtRequest, isPending: isSubmitting } =
    useRemoveIcbtRequest();

  const handleRemove = async () => {
    try {
      if (!selectedRow) {
        throw new Error("Removal handler was called without selecting a row!");
      }
      if (!userId) {
        throw new Error("No user id to handle deletion!");
      }

      if (!selectedRow.metadata) {
        throw new Error("No metadata to handle deletion!");
      }

      await removeIcbtRequest({
        requestId: selectedRow.metadata.requestId,
        userId: userId.toString(),
        programId: selectedRow.metadata.programId,
      });
      enqueueSnackbar({ text: t("DELETE_ASSIGN_SUCCESS") });
    } catch {
      enqueueSnackbar({ text: t("DELETE_ASSIGN_FAILURE"), isError: true });
    } finally {
      setSelectedRow(undefined);
    }
  };

  const hasForbiddenError =
    isError && Object.values(possiblyErrors).some(isForbiddenError);

  if (isLoading || !patient) return <Spinner />;

  const getThreeDotsState = (row: AssignedTableRow) => {
    if (!row.metadata)
      return {
        removeActionEnabled: false,
        feedbackDateEditActionEnabled: false,
      };
    // NOTE: Only latest assignments of non-started programs should be removable. Also, no feedback yet.
    const removeActionEnabled =
      isPsychologist &&
      !row.metadata.hasProgress &&
      row.metadata.isLatestRequest &&
      row.metadata.hasNoFeedback;

    const feedbackDateEditActionEnabled = row.metadata.canExtendFeedbackDate;

    return {
      removeActionEnabled,
      feedbackDateEditActionEnabled,
    };
  };

  const feedbackOverDueCount = data.assignedTableRows.filter(
    (row: AssignedTableRow) => {
      if (!row.metadata) return false;

      return (
        row.metadata.hasNoFeedback &&
        row.metadata.feedbackDate &&
        !row.metadata.icbtFeedbackDateIgnored &&
        isPast(new Date(row.metadata.feedbackDate)) &&
        !isToday(new Date(row.metadata.feedbackDate))
      );
    },
  ).length;

  const notification = getNotification({
    assignedNewProgressCount: data.assignedNewProgressCount,
    feedbackOverDueCount,
    t,
  });

  return (
    <>
      <IcbtHeaderContainer>
        <Typography insensitive variant="h3">
          {t("ASSIGN_ICBT_HEADER")}
        </Typography>
        <AddButton
          data-testid="assignIcbtButton_bookingModal"
          variant="primary"
          onClick={() => setAssignIcbtModalIsOpen(!assignIcbtModalIsOpen)}
          text={t("ASSIGN_ICBT")}
          disabled={isError}
        />
      </IcbtHeaderContainer>
      <SaveSnackbar
        open={hasDraggedItems}
        onCancel={handleSortCancel}
        onSave={handleSortSave}
        isLoading={isSortingMutating}
        saveLabel={t("SAVE_CHANGES")}
        cancelLabel={t("CANCEL")}
      >
        {t("Save changes to the program order?")}
      </SaveSnackbar>

      {(() => {
        if (isError)
          return (
            <EmptyContainer>
              <Typography
                variant="body"
                color={"GRAY_DARK"}
                sx={{ paddingLeft: "8px" }}
              >
                {hasForbiddenError
                  ? t("ICBT_LOADING_DATA_ERROR_FORBIDDEN")
                  : t("ICBT_LOADING_DATA_ERROR")}
              </Typography>
            </EmptyContainer>
          );
        return (
          <>
            <ExpandableSection expanded={true}>
              <Toggle>
                <Typography insensitive variant="h4">
                  {t("ASSIGNMENTS")}
                </Typography>
                <TypographyWithBefore
                  variant="body"
                  color="GRAY_DARK"
                  data-testid="noOfAssignedPrograms"
                  insensitive
                >
                  {`${data.assignedTableRows.length} ${t(
                    "PROGRAMS",
                  ).toLowerCase()}`}
                </TypographyWithBefore>
                {notification ? (
                  <RightIconContainer>
                    <StatusIndicator variant={defaultTheme.COLORS.ERROR} />
                    <Typography variant="body" color="BLACK_GREY">
                      {notification.text}
                    </Typography>
                  </RightIconContainer>
                ) : null}
              </Toggle>
              <ExpandableSection.Content>
                <DndComponents.RootContext>
                  <Table
                    columns={assignedTableColumns}
                    data={tableData ?? []}
                    rowContext={DndComponents.SortableContext}
                    rowComponent={DndComponents.RowComponent}
                    onRowClick={(row: AssignedTableRow) => {
                      if (!row.metadata) return;

                      handleClickOnRow({
                        programId: row.metadata.programId,
                        treatmentId: row.metadata.treatmentId,
                        icbtRequestId: row.metadata.icbtRequestId,
                        feedbackDueDate: row.metadata.feedbackDueDate,
                        feedbackDueDateIgnored:
                          row.metadata.feedbackDueDateIgnored,
                      });
                    }}
                    styles={{
                      rowPadding: "none",
                      noMargin: true,
                    }}
                    rowAction={(row: AssignedTableRow) => {
                      const {
                        feedbackDateEditActionEnabled,
                        removeActionEnabled,
                      } = getThreeDotsState(row);
                      if (
                        !removeActionEnabled &&
                        !feedbackDateEditActionEnabled
                      ) {
                        return null;
                      }

                      return (
                        <Menu>
                          <Menu.Trigger
                            variant="icon"
                            testId="removeIcbtEllipsis"
                          >
                            <Icon.MoreVert />
                          </Menu.Trigger>
                          <MenuItems>
                            {feedbackDateEditActionEnabled && (
                              <Menu.Item
                                onClick={() => {
                                  if (!row.metadata) return;

                                  setEditFeedbackDateOpen({
                                    feedbackDate: row.metadata.feedbackDate,
                                    requestId: row.metadata.requestId,
                                  });
                                }}
                              >
                                {t("EDIT_ICBT_FEEDBACK_DATE")}
                              </Menu.Item>
                            )}
                            {removeActionEnabled && (
                              <Menu.Item
                                onClick={() => {
                                  setSelectedRow(row);
                                  setConfirmOpen(true);
                                }}
                              >
                                {t("REMOVE")}
                              </Menu.Item>
                            )}
                          </MenuItems>
                        </Menu>
                      );
                    }}
                  />
                </DndComponents.RootContext>

                {/* empty span to scroll to, so we don't have to target the last row in table above */}
                <Spacing t={8} />
                <AddButton
                  variant="neutral"
                  onClick={() =>
                    setAssignIcbtModalIsOpen(!assignIcbtModalIsOpen)
                  }
                  text={t("ASSIGN_ICBT")}
                  data-testid="assignIcbtButton_patientDetails"
                />
              </ExpandableSection.Content>
            </ExpandableSection>

            <ExpandableSection>
              <Toggle>
                <Typography insensitive variant="h4">
                  {t("INITIATED_BY_PATIENT")}
                </Typography>
                <TypographyWithBefore
                  variant="body"
                  color="GRAY_DARK"
                  data-testid="noOfAssignedPrograms"
                  insensitive
                >
                  {`${data.selfExploredTableRows.length} ${t(
                    "PROGRAMS",
                  ).toLowerCase()}`}
                </TypographyWithBefore>

                {notification ? (
                  <RightIconContainer>
                    <StatusIndicator
                      variant={defaultTheme.COLORS.ACTION_BLUE}
                    />
                    <Typography variant="body" color="BLACK_GREY">
                      {`${data.selfExploredNewProgressCount} ${t(
                        "RECENTLY_WORKED_ON",
                      )}`}
                    </Typography>
                  </RightIconContainer>
                ) : null}
              </Toggle>
              <ExpandableSection.Content>
                <Table
                  columns={selfExploredTableColumns}
                  data={data?.selfExploredTableRows ?? []}
                  styles={{
                    rowPadding: "none",
                    noMargin: true,
                  }}
                />
              </ExpandableSection.Content>
            </ExpandableSection>
            {assignIcbtModalIsOpen && (
              <AssignIcbtModal
                patient={patient}
                closeModal={handleAssignIcbtModalClose}
              />
            )}
            <ConfirmRemoveAssignModal
              setModalIsOpen={setConfirmOpen}
              open={confirmOpen}
              handleRemove={handleRemove}
              isSubmitting={isSubmitting}
              patientName={`${patient.firstName} ${patient.lastName}`}
            />
            {editFeedbackDateOpen?.requestId && patientUserId && (
              <EditFeedbackDateModal
                intialDate={
                  editFeedbackDateOpen?.feedbackDate
                    ? new Date(editFeedbackDateOpen.feedbackDate)
                    : undefined
                }
                requestId={editFeedbackDateOpen.requestId}
                close={() => setEditFeedbackDateOpen(null)}
                patientUserId={patient?.userId}
              />
            )}
            {programAndTreatmentIdToPreview && programPreviewModalOpen && (
              <PreviewProgramModal
                handleCloseProgramPreviewModal={handleCloseProgramPreviewModal}
                open={programPreviewModalOpen}
                programId={programAndTreatmentIdToPreview.programId}
              />
            )}
            {userId &&
              programAndTreatmentIdToPreview &&
              programAndTreatmentIdToPreview.icbtRequestId &&
              feedbackModalOpen && (
                <FeedbackModal
                  patientSsn={patient?.nationalIdentity}
                  handleCloseFeedbackModal={handleCloseProgramPreviewModal}
                  programId={programAndTreatmentIdToPreview.programId}
                  treatmentId={programAndTreatmentIdToPreview.treatmentId}
                  open={feedbackModalOpen}
                  userId={userId}
                  icbtRequestId={programAndTreatmentIdToPreview.icbtRequestId}
                  patientName={`${patient.firstName} ${patient.lastName}`}
                  setAssignIcbtModalIsOpen={setAssignIcbtModalIsOpen}
                  icbtFeedbackDate={
                    programAndTreatmentIdToPreview.feedbackDueDate
                      ? String(programAndTreatmentIdToPreview.feedbackDueDate)
                      : undefined
                  }
                  icbtFeedbackDateIgnored={
                    programAndTreatmentIdToPreview.feedbackDueDateIgnored
                  }
                  isLocked={getIsSequentiallyLocked(
                    icbtSequentialUnlock,
                    programAndTreatmentIdToPreview.programId,
                    programAndTreatmentIdToPreview.treatmentId,
                    programAndTreatmentIdToPreview.icbtRequestId,
                  )}
                  isFeedbackLocked={
                    feedbackLockEnabled &&
                    getIsFeedbackLocked(
                      icbtSequentialUnlock,
                      programAndTreatmentIdToPreview.programId,
                      programAndTreatmentIdToPreview.treatmentId,
                      programAndTreatmentIdToPreview.icbtRequestId,
                    )
                  }
                  feedbackLockReason={getFeedbackLockReason(
                    icbtSequentialUnlock,
                    programAndTreatmentIdToPreview.programId,
                    programAndTreatmentIdToPreview.treatmentId,
                    programAndTreatmentIdToPreview.icbtRequestId,
                  )}
                  isRequestOwner={getIsRequestOwner(
                    icbtSequentialUnlock,
                    programAndTreatmentIdToPreview.icbtRequestId,
                    loggedInPsychologistId,
                  )}
                  requestingPsychologistId={getRequestingPsychologistId(
                    icbtSequentialUnlock,
                    programAndTreatmentIdToPreview.programId,
                    programAndTreatmentIdToPreview.treatmentId,
                    programAndTreatmentIdToPreview.icbtRequestId,
                  )}
                  assessment={getAssessment(
                    icbtSequentialUnlock,
                    programAndTreatmentIdToPreview.programId,
                    programAndTreatmentIdToPreview.treatmentId,
                    programAndTreatmentIdToPreview.icbtRequestId,
                  )}
                />
              )}
          </>
        );
      })()}
    </>
  );
};

const MenuItems = styled(Menu.Items)`
  transform: translateX(-50%);
`;

const Toggle = styled(ExpandableSection.Toggle)`
  padding: 12px;
`;

const TypographyWithBefore = styled(Typography)`
  &:before {
    content: "-";
    padding: 0 4px;
    width: fit-content;
  }
`;

const RightIconContainer = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
`;
